type Value = Value[] | number | string | Record<string, any>;
type Options = {
    skipNull?: boolean
    skipEmptyString?: boolean
}

/**
 * @typedef {Object.<string, boolean>} Options
 * @description All the available options for {@link jsObjectToApi}
 * @property {boolean} [skipNull] `null` values are ignored
 * @property {boolean} [skipEmptyString] Empty strings are ignored
 */
/**
 * @function jsObjectToApi
 * @description
 * Transform a JS object to an object formatted for api values.<br>
 * - `null` values are transformed to empty strings except when {@link Options}.skipNull is `true`
 * - Boolean values are transform to `'1'` or `'0'`
 * <br>
 * Please note that the convinience of this function comes with a much slower process. Use this function only with small to medium data sets.
 * @param {Object} jsObject The data to transform
 * @param {URLSearchParams} [searchParamObject] The {@link https://developer.mozilla.org/fr/docs/Web/API/URLSearchParams URLSearchParams} object on which the function will automatically append the data
 * @param {Options} [options={}] Formatting options
 * @returns {Object.<string, string>} The new formatted object
 * @example
 * jsObjectToApi({
 *     emptyString: '',
 *     name: 'the name here',
 *     i18n: {
 *         fr: {
 *             name: 'Oui oui nom'
 *         },
 *         en: {
 *             name: 'Yes yes name'
 *         },
 *     },
 *     myObject: {
 *         title: 'My object\'s title',
 *         stringArray: ['string 1', 'string 2', 3],
 *         innerObject: {
 *             innerArray: [
 *                 {
 *                     linked: true,
 *                     id: 'as123fg-23dfg'
 *                 },
 *                 {
 *                     linked: false,
 *                     id: '46rghdf-23d32aefgfg'
 *                 },
 *             ]
 *         }
 *     },
 *     array: [
 *         {
 *             id: 'object1',
 *             active: true,
 *             nullValue: null,
 *         },
 *         {
 *             id: 'object2',
 *             active: false,
 *             nullValue: null,
 *             list: [{
 *                 name: 'the name here',
 *                 i18n: {
 *                     fr: {
 *                         name: 'Oui oui nom'
 *                     },
 *                     en: {
 *                         name: 'Yes yes name'
 *                     },
 *                 },
 *             }]
 *         },
 *     ]
 * }, searchParams, {skipNull: true})
 *
 * // Returned value
 * {
 *     "emptyString": "",
 *     "name": "the name here",
 *     "i18n[fr][name]": "Oui oui nom",
 *     "i18n[en][name]": "Yes yes name",
 *     "myObject[title]": "My object's title",
 *     "myObject[stringArray][0]": "string 1",
 *     "myObject[stringArray][1]": "string 2",
 *     "myObject[stringArray][2]": "3",
 *     "myObject[innerObject][innerArray][0][linked]": "1",
 *     "myObject[innerObject][innerArray][0][id]": "as123fg-23dfg",
 *     "myObject[innerObject][innerArray][1][linked]": "0",
 *     "myObject[innerObject][innerArray][1][id]": "46rghdf-23d32aefgfg",
 *     "array[0][id]": "object1",
 *     "array[0][active]": "1",
 *     "array[1][id]": "object2",
 *     "array[1][active]": "0",
 *     "array[1][list][0][name]": "the name here",
 *     "array[1][list][0][i18n][fr][name]": "Oui oui nom",
 *     "array[1][list][0][i18n][en][name]": "Yes yes name"
 * }
 * @copyright Spordle
 */
export function jsObjectToApi(jsObject: Record<string, Value>, searchParamObject?: URLSearchParams, options: Options = {}): Record<string, string>{
    let formattedObject: Record<string, string> = {};
    for (const key in jsObject) {
        if (Object.prototype.hasOwnProperty.call(jsObject, key)) {
            const newData = format(jsObject[key], key, options);
            formattedObject = {...formattedObject, ...newData};
            if(searchParamObject){
                appendValues(newData, searchParamObject);
            }
        }
    }

    return formattedObject;
}

function format(data: Value, trail: string, options: Options): Record<string, string>{
    if(Array.isArray(data)){
        return formatArray(data, trail, options);
    }
    else if(typeof data === 'object' && data !== null){// null is considered as an object in JS
        return formatObject(data, trail, options);
    }
    else if(typeof data === "boolean"){
        return {[trail]: (!!data as any >>> 0).toString()};// 1 for true and 0 for false
    }else{
        if(data == null){
            if(!options.skipNull)
                return {[trail]: ''};
        }
        else if(typeof data === "string"){
            if(!(!data && options.skipEmptyString)){// Empty string
                return {[trail]: data};
            }
        }
        else{// Such as numbers
            return {[trail]: data?.toString?.()};
        }
    }
    return {};
}

function formatObject(object: Record<string, Value>, trail: string, options: Options): Record<string, string>{
    let formattedObject = {};
    for (const key in object) {
        if (Object.prototype.hasOwnProperty.call(object, key)) {
            formattedObject = {...formattedObject, ...format(object[key], `${trail}[${key}]`, options)};
        }
    }
    return formattedObject;
}

function formatArray(list: Value[], trail: string, options: Options): Record<string, string>{
    return list.reduce((formattedArray: Record<string, string>, data, index) => ({...formattedArray, ...format(data, `${trail}[${index}]`, options)}), {});
}

function appendValues(values: Record<string, string>, searchParamObject: URLSearchParams){
    for (const key in values) {
        if (Object.prototype.hasOwnProperty.call(values, key)) {
            searchParamObject.append(key, values[key]);
        }
    }
}